import { cn } from '@/lib/cn';
import { type SessionClinicalEntity } from '@/types';
import { CodeSystemLogo, getHccCode, HccBadge } from '@heidi/coding';
import {
  useMutateConfirmEmbeddingStatus,
  useMutateUnconfirmEmbeddingStatus,
} from '@heidi/coding/data';
import { Button } from '@heidi/design-system/button';
import { Checkbox } from '@heidi/design-system/checkbox';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '@heidi/design-system/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { TypographyFootNote, TypographyP2Bold } from '@heidi/design-system/typography';
import { CodedClinicalEntityStatus } from '@heidi/schema/ml-scribe';
import { ExpandableText } from '@heidi/snowflake-ui/expandable-text';
import { CheckSquare, CodeSquare, Info } from 'lucide-react';
import { memo } from 'react';
import { useIntl } from 'react-intl';

const codeChipClasses = cn(
  'flex h-auto w-fit whitespace-normal py-1 text-start',
  'bg-validation-success-muted text-text-primary',
  'hover:bg-validation-success hover:text-text-primary-invert',
  'active:bg-validation-success disabled:bg-validation-success-muted'
);

interface CodeItemProps {
  code: SessionClinicalEntity;
  tooltipText?: string;
  sessionId: string;
  handleViewCodeDetail: (code: SessionClinicalEntity) => void;
  handleOpenEmbeddingsList: () => void;
}

export const CodeItem = memo(
  ({
    code,
    tooltipText,
    sessionId,
    handleViewCodeDetail,
    handleOpenEmbeddingsList,
  }: CodeItemProps) => {
    const intl = useIntl();
    const { mutate: confirmEmbeddingStatus } = useMutateConfirmEmbeddingStatus();
    const { mutate: unconfirmEmbeddingStatus } = useMutateUnconfirmEmbeddingStatus();
    const confirmCodesLabel = intl.formatMessage({
      defaultMessage: 'Confirm codes',
      id: 'tSbK0Wvofc',
      description: 'Accessible label for confirming a suggested clinical code',
    });
    const viewCodeSpecificationLabel = intl.formatMessage({
      defaultMessage: 'View code specification',
      id: 'W7viXFELEa',
      description: 'Label for opening the details of a clinical code',
    });

    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button size="xxs" className={cn(codeChipClasses, 'group')}>
            <span className="leading-5">
              {code.primary_code.code_name} - {code.primary_code.code}
            </span>
            <CheckSquare className="shrink-0 text-validation-success transition-colors group-hover:text-text-primary-invert" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-fit rounded-lg !p-0" side="right">
          <div
            key={code.entity_id}
            className={cn(
              'flex max-w-[400px] cursor-pointer items-center justify-between rounded-lg border bg-surface p-3 shadow-sm transition-colors duration-300 hover:border-border-selected',
              code.status === CodedClinicalEntityStatus.REJECTED &&
                'border-validation-destructive hover:border-validation-destructive-hover',
              code.status === CodedClinicalEntityStatus.CONFIRMED &&
                'border-validation-success hover:border-validation-success'
            )}
          >
            <div className="flex items-start gap-2">
              <Checkbox
                className="mt-1"
                checked={code.status === CodedClinicalEntityStatus.CONFIRMED}
                aria-label={confirmCodesLabel}
                onCheckedChange={(checked) => {
                  if (!code.entity_id) return;

                  if (checked === true) {
                    void confirmEmbeddingStatus({
                      sessionId: sessionId,
                      codes: [
                        {
                          code: code.primary_code.code,
                          code_system: code.primary_code.code_system,
                          entity_id: code.entity_id,
                        },
                      ],
                    });
                  } else if (checked === false) {
                    void unconfirmEmbeddingStatus({
                      sessionId: sessionId,
                      entityIds: [code.entity_id],
                    });
                  }
                }}
              />

              {/* Code Details */}
              <div className="flex flex-col gap-1">
                <div className="flex items-center">
                  <div className="rounded-md bg-surface-page p-1">
                    <CodeSystemLogo codeSystem={code.primary_code.code_system} />
                  </div>
                  <TypographyP2Bold className="ms-1">
                    {code.primary_code.code}
                  </TypographyP2Bold>
                  <HccBadge
                    codeSystem={code.primary_code.code_system}
                    hccCode={getHccCode(code.primary_code)}
                  />
                  <Tooltip delayDuration={100}>
                    <TooltipTrigger asChild>
                      <PopoverClose asChild>
                        <Button
                          disableAutomaticSvgSizing
                          size="xs"
                          variant="ghost"
                          className="ms-1 h-fit min-w-fit p-[2px] px-1"
                          aria-label={tooltipText || viewCodeSpecificationLabel}
                          onClick={() => handleViewCodeDetail(code)}
                        >
                          <Info size={14} />
                        </Button>
                      </PopoverClose>
                    </TooltipTrigger>
                    <TooltipContent>
                      {tooltipText || viewCodeSpecificationLabel}
                    </TooltipContent>
                  </Tooltip>
                </div>
                <ExpandableText text={code.primary_code.code_name} />

                {code.reference_in_transcript?.slice(0, 3).map((t, i) => (
                  <TypographyFootNote
                    className="text-text-secondary"
                    key={`transcript_text-${i}-${t.transcript_text}`}
                  >
                    &quot;{t.transcript_text} &quot;
                  </TypographyFootNote>
                ))}
                <PopoverClose asChild>
                  <Button
                    variant="ghost"
                    size="xs"
                    flush="start"
                    className="w-fit text-text-secondary"
                    onClick={handleOpenEmbeddingsList}
                  >
                    <CodeSquare />
                    {intl.formatMessage({
                      defaultMessage: 'Review and edit codes',
                      id: 'i9pYlk0wCs',
                      description: 'Action to open the list of suggested clinical codes',
                    })}
                  </Button>
                </PopoverClose>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);

CodeItem.displayName = 'CodeItem';
